import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Save, Shuffle } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ASSIGNMENT_FIELDS_URL, ASSIGNMENT_RETRY_URL, ASSIGNMENT_SETTINGS_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import RulesTab from "../../components/assignment/RulesTab";
import TeamsTab from "../../components/assignment/TeamsTab";
import HistoryTab from "../../components/assignment/HistoryTab";
import { Field, Stat, Switch, Tabs, btnOutline, btnPrimary, inputCls } from "../../components/assignment/shared";

/**
 * Settings -> Lead Assignment (trustedge customers/assignment/).
 *
 * Who gets each new lead. Simple mode is the Overview form alone: one team,
 * a strategy, limits and a fallback - five minutes to set up. Advanced mode
 * puts ordered rules in front of it (Rules tab). Both modes share the same
 * stored settings, so switching never loses anything.
 */

const EDITABLE = ["is_enabled", "mode", "default_team_id", "default_strategy", "daily_cap", "open_cap",
    "fallback_action", "fallback_user_id", "keep_existing_owner", "manual_blank_action", "import_action",
    "unassigned_alert_minutes", "reps_can_claim", "require_reassign_reason", "unanswered_reassign_after"];

const STRATEGIES = [
    ["round_robin", "Round robin", "Each rep in turn: A, B, C, A…"],
    ["weighted", "Weighted", "Reps with a higher weight get more"],
    ["least_busy", "Least busy", "Whoever has the fewest open leads"],
];

export default function LeadAssignment() {
    const { fetch, update, create } = useApiMethods();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(null);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("overview");
    const [meta, setMeta] = useState({ fields: [], triggers: [], rule_fallbacks: [] });
    const [backlog, setBacklog] = useState(0);
    const [loadError, setLoadError] = useState("");

    const load = () => fetch(ASSIGNMENT_SETTINGS_URL).then(res => {
        if (res?.status === "success") { setData(res); setDraft(res.settings); setLoadError(""); }
        else setLoadError(res?.message || "Could not load lead assignment");
    });
    useEffect(() => {
        load();
        fetch(ASSIGNMENT_FIELDS_URL).then(res => { if (res?.status === "success") setMeta(res); });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (loadError) return <ContainerCard><p className="p-6 text-xs text-gray-500">{loadError}</p></ContainerCard>;
    if (!draft) return <ContainerCard><p className="p-6 text-xs text-gray-400">Loading…</p></ContainerCard>;

    const canEdit = data.can_edit;
    const dirty = EDITABLE.some(k => String(draft[k] ?? "") !== String(data.settings[k] ?? ""));
    const set = (patch) => setDraft(d => ({ ...d, ...patch }));
    const team = data.teams.find(t => t.id === Number(draft.default_team_id));

    const save = async () => {
        if (draft.mode === "simple" && data.settings.mode === "advanced"
            && !window.confirm("Switch to Simple mode?\n\nYour rules are kept but stop running until you switch back to Advanced.")) return;
        if (draft.is_enabled && !data.settings.is_enabled
            && !window.confirm(`Turn on automatic assignment?\n\nFrom now on new leads from every source go to ${team?.name || "the chosen"} team.`)) return;
        setSaving(true); setErrors({});
        try {
            const body = Object.fromEntries(EDITABLE.map(k => [k, draft[k]]));
            body.version = data.settings.version;
            const res = await update(ASSIGNMENT_SETTINGS_URL, body);
            if (res?.status === "success") {
                const next = res.next_in_line ? ` The next lead goes to ${res.next_in_line.name}.` : "";
                ToastMsg("success", `${res.message}.${next}`);
                // A new Needs Reassignment threshold is applied to leads already in Unanswered.
                if (res.reassign_flagged || res.reassign_cleared) {
                    const parts = [];
                    if (res.reassign_flagged) parts.push(`${res.reassign_flagged} lead${res.reassign_flagged === 1 ? "" : "s"} now need reassignment`);
                    if (res.reassign_cleared) parts.push(`${res.reassign_cleared} no longer flagged`);
                    ToastMsg("info", parts.join(" · "));
                }
                setData(res); setDraft(res.settings);
                if (res.settings.is_enabled && !data.settings.is_enabled && res.backlog) setBacklog(res.backlog);
            } else if (res?.code === "version_conflict") {
                ToastMsg("error", res.message); load();
            } else {
                setErrors(res?.errors || {});
                ToastMsg("error", res?.message || "Could not save");
            }
        } finally { setSaving(false); }
    };

    const assignBacklog = async () => {
        const res = await create(ASSIGNMENT_RETRY_URL, { backlog: true });
        if (res?.status === "success") { ToastMsg("success", res.message); setBacklog(0); load(); }
        else ToastMsg("error", res?.message || "Could not assign the waiting leads");
    };

    const s = data.stats;
    const tabs = [
        { key: "overview", label: "Overview" },
        { key: "rules", label: "Rules" },
        { key: "teams", label: "Teams & people", count: data.teams.length },
        { key: "history", label: "History" },
    ];

    return (
        <ContainerCard>
            <div className="p-4 space-y-4 overflow-auto">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Shuffle className="h-5 w-5 text-primary" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Lead Assignment</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Who gets each new lead — from Meta, the website, Zapier, referrals, imports and the Add Lead form.
                            </p>
                        </div>
                    </div>
                    {tab === "overview" && canEdit && (
                        <div className="flex gap-2">
                            {dirty && <button type="button" className={btnOutline} onClick={() => { setDraft(data.settings); setErrors({}); }}>Discard</button>}
                            <button type="button" onClick={save} disabled={!dirty || saving} className={btnPrimary}>
                                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                    )}
                </div>

                <Tabs tabs={tabs} value={tab} onChange={setTab} />

                {tab === "overview" && (
                    <div className="space-y-4">
                        {backlog > 0 && (
                            <div className="rounded-lg border border-brand-200 bg-brand-50/60 dark:bg-brand-900/10 dark:border-brand-800 p-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                                <span className="text-gray-700 dark:text-gray-200">
                                    {backlog} open lead{backlog === 1 ? " is" : "s are"} already waiting without an owner. Assign {backlog > 500 ? "the oldest 500" : "them"} now with these settings?
                                </span>
                                <div className="flex gap-2">
                                    <button type="button" className={btnOutline} onClick={() => setBacklog(0)}>Leave them</button>
                                    <button type="button" className={btnPrimary} onClick={assignBacklog}>Assign now</button>
                                </div>
                            </div>
                        )}

                        <div className={cn("rounded-xl border p-4 flex items-start justify-between gap-4",
                            draft.is_enabled ? "border-brand-200 bg-brand-50/60 dark:bg-brand-900/10 dark:border-brand-800/50"
                                             : "border-gray-200 dark:border-gray-700")}>
                            <div className="text-xs">
                                <p className="font-semibold text-gray-900 dark:text-white text-sm">
                                    Assign new leads automatically — {draft.is_enabled ? "ON" : "OFF"}
                                </p>
                                <p className="text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
                                    {draft.is_enabled
                                        ? <>Order: <b>{data.chain}</b>.{data.next_in_line && <> The next lead goes to <b>{data.next_in_line.name}</b>.</>}</>
                                        : "Off: leads arrive as they do today — webhook and referral leads unowned, imports owned by the importer, the Add Lead form by whoever is picked."}
                                </p>
                            </div>
                            <Switch checked={draft.is_enabled} disabled={!canEdit} onChange={v => set({ is_enabled: v })} label="Automatic assignment" />
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <Stat label="Assigned automatically today" value={s.auto_today} />
                            <Stat label="Assigned by hand today" value={s.manual_today} />
                            <Stat label="Unassigned right now" value={s.unassigned_now} tone={s.unassigned_now ? "warn" : undefined}
                                hint={s.waiting_engine ? `${s.waiting_engine} waiting for someone to free up` : undefined} />
                            <Stat label="Failed, will retry" value={s.failed} tone={s.failed ? "bad" : undefined} />
                        </div>
                        {(s.unassigned_now > 0) && (
                            <button type="button" onClick={() => navigate("/customers/unassigned")}
                                className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                                Open the unassigned queue <ArrowRight className="h-3 w-3" />
                            </button>
                        )}

                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {draft.mode === "advanced" ? "Default — when no rule matches" : "Who gets new leads"}
                                </p>
                                <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden text-xs">
                                    {[["simple", "Simple"], ["advanced", "Advanced (rules)"]].map(([k, l]) => (
                                        <button key={k} type="button" disabled={!canEdit} onClick={() => set({ mode: k })}
                                            className={cn("px-3 py-1", draft.mode === k ? "bg-primary text-white" : "text-gray-600 dark:text-gray-300")}>{l}</button>
                                    ))}
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <Field label="Team" error={errors.default_team_id}
                                    help={data.teams.length ? "Create or edit teams on the Teams & people tab." : undefined}>
                                    {data.teams.length ? (
                                        <select value={draft.default_team_id || ""} disabled={!canEdit}
                                            onChange={e => set({ default_team_id: e.target.value ? Number(e.target.value) : null })}
                                            className={inputCls}>
                                            <option value="">Pick a team…</option>
                                            {data.teams.filter(t => t.is_active).map(t => <option key={t.id} value={t.id}>{t.name} ({t.members} member{t.members === 1 ? "" : "s"})</option>)}
                                        </select>
                                    ) : (
                                        <button type="button" className={btnOutline} onClick={() => setTab("teams")}>Create the first team</button>
                                    )}
                                </Field>
                                <Field label="If nobody in the team is free" error={errors.fallback_action || errors.fallback_user_id}>
                                    <div className="flex gap-2">
                                        <select value={draft.fallback_action} disabled={!canEdit}
                                            onChange={e => set({ fallback_action: e.target.value })} className={inputCls}>
                                            {(data.choices.fallback_action || []).map(c => <option key={c.value} value={c.value}>{c.label}{c.value === "team_manager" && team?.manager ? ` (${team.manager.name})` : ""}</option>)}
                                        </select>
                                        {draft.fallback_action === "user" && (
                                            <select value={draft.fallback_user_id || ""} disabled={!canEdit}
                                                onChange={e => set({ fallback_user_id: e.target.value ? Number(e.target.value) : null })} className={inputCls}>
                                                <option value="">Pick a person…</option>
                                                {data.users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                            </select>
                                        )}
                                    </div>
                                </Field>
                            </div>

                            <Field label="How to share leads">
                                <div className="grid sm:grid-cols-3 gap-2">
                                    {STRATEGIES.map(([k, l, h]) => (
                                        <label key={k} className={cn("rounded-md border p-2 text-xs", canEdit && "cursor-pointer",
                                            draft.default_strategy === k ? "border-primary bg-brand-50/60 dark:bg-brand-900/20" : "border-gray-200 dark:border-gray-700")}>
                                            <input type="radio" className="mr-1" disabled={!canEdit} checked={draft.default_strategy === k}
                                                onChange={() => set({ default_strategy: k })} />
                                            <b className="text-gray-800 dark:text-gray-100">{l}</b>
                                            <p className="text-[11px] text-gray-500 mt-0.5">{h}</p>
                                        </label>
                                    ))}
                                </div>
                            </Field>

                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <Field label="Daily limit per rep" help="Blank = no limit. A rep's own limit wins." error={errors.daily_cap}>
                                    <input type="number" min={1} max={500} value={draft.daily_cap ?? ""} disabled={!canEdit}
                                        onChange={e => set({ daily_cap: e.target.value === "" ? null : Number(e.target.value) })} className={inputCls} />
                                </Field>
                                <Field label="Open leads per rep" help="Blank = no limit." error={errors.open_cap}>
                                    <input type="number" min={1} max={5000} value={draft.open_cap ?? ""} disabled={!canEdit}
                                        onChange={e => set({ open_cap: e.target.value === "" ? null : Number(e.target.value) })} className={inputCls} />
                                </Field>
                                <Field label="Alert managers after (minutes)" help="A lead left unassigned this long." error={errors.unassigned_alert_minutes}>
                                    <input type="number" min={5} max={1440} value={draft.unassigned_alert_minutes ?? 15} disabled={!canEdit}
                                        onChange={e => set({ unassigned_alert_minutes: e.target.value === "" ? 15 : Number(e.target.value) })} className={inputCls} />
                                </Field>
                                <Field label="Imported leads go to">
                                    <select value={draft.import_action} disabled={!canEdit}
                                        onChange={e => set({ import_action: e.target.value })} className={inputCls}>
                                        {(data.choices.import_action || []).map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                </Field>
                            </div>

                            <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                                {[["keep_existing_owner", "Repeat enquiries go back to the rep who already has the person", "Unless that rep is away. Limits do not apply to a returning lead."],
                                  ["reps_can_claim", "Reps can take unassigned leads themselves", "From Leads → Unassigned, within their daily limit."],
                                  ["require_reassign_reason", "Ask for a reason when moving a lead to someone else", "Shown in the owner history."]].map(([k, l, h]) => (
                                    <label key={k} className="flex items-start gap-2 text-xs">
                                        <input type="checkbox" className="mt-0.5" disabled={!canEdit} checked={!!draft[k]} onChange={e => set({ [k]: e.target.checked })} />
                                        <span><span className="font-medium text-gray-800 dark:text-gray-100">{l}</span>
                                            <span className="block text-[11px] text-gray-500">{h}</span></span>
                                    </label>
                                ))}
                                <label className="flex items-start gap-2 text-xs">
                                    <input type="checkbox" className="mt-0.5" disabled={!canEdit} checked={draft.manual_blank_action === "engine"}
                                        onChange={e => set({ manual_blank_action: e.target.checked ? "engine" : "unassigned" })} />
                                    <span><span className="font-medium text-gray-800 dark:text-gray-100">Leads added by hand with no owner are assigned too</span>
                                        <span className="block text-[11px] text-gray-500">Picking an owner on the Add Lead form always wins.</span></span>
                                </label>
                            </div>
                        </div>
                        {/* Needs Reassignment — independent of automatic assignment being on */}
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div className="max-w-xl">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Unanswered leads</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        {Number(draft.unanswered_reassign_after) > 0
                                            ? <>When a rep marks a lead <b>Unanswered</b> {draft.unanswered_reassign_after} time{Number(draft.unanswered_reassign_after) === 1 ? "" : "s"} in a row,
                                                it is flagged <b>Needs Reassignment</b> and admins are notified. The flag clears when the lead is reassigned or moves out of Unanswered.</>
                                            : <>Off — leads are never flagged for reassignment, however often they go unanswered.</>}
                                    </p>
                                    <p className="text-[11px] text-gray-400 mt-1">Works whether or not automatic assignment is on. Changing the number re-checks leads already in Unanswered.</p>
                                </div>
                                <div className="flex items-end gap-4">
                                    <Field label="Flag after (unanswered attempts)" help="0 = off" error={errors.unanswered_reassign_after}>
                                        <input type="number" min={0} max={50} value={draft.unanswered_reassign_after ?? 5} disabled={!canEdit}
                                            aria-label="Flag for reassignment after this many unanswered attempts"
                                            onChange={e => set({ unanswered_reassign_after: e.target.value === "" ? 0 : Number(e.target.value) })}
                                            className={cn(inputCls, "w-28")} />
                                    </Field>
                                    <Stat label="Flagged right now" value={s.needs_reassignment ?? 0} tone={s.needs_reassignment ? "warn" : undefined} />
                                </div>
                            </div>
                        </div>

                        {!canEdit && <p className="text-[11px] text-gray-400">You can view these settings; changing them needs the Lead Assignment edit permission.</p>}
                    </div>
                )}

                {tab === "rules" && <RulesTab meta={meta} teams={data.teams} users={data.users} setting={data.settings} onChanged={load} />}
                {tab === "teams" && <TeamsTab users={data.users} onChanged={load} />}
                {tab === "history" && <HistoryTab users={data.users} teams={data.teams} />}
            </div>
        </ContainerCard>
    );
}
